import React from 'react';
import countryConfigData from './country-config';
import {
  readLanguageCookie,
  clearLanguageCookie,
} from '../utils/languagePreference';

const STORAGE_KEY = 'mtp:selected-country';
export const DEFAULT_COUNTRY = countryConfigData.defaultCountry || 'gb';
export const COUNTRY_CONFIG = countryConfigData.countries || {};
const SUPPORTED_COUNTRIES = Object.keys(COUNTRY_CONFIG);

// Map language codes to country codes for gatsby-plugin-react-i18next URLs
const LANGUAGE_TO_COUNTRY = {
  'en-GB': 'gb',
  'en-US': 'us',
  'en-AU': 'au',
  'en-CA': 'ca',
  'fr-FR': 'fr',
  'de-DE': 'de',
  'es-ES': 'es',
  'es-MX': 'mx',
  'it-IT': 'it',
  'pt-BR': 'br',
  'hi-IN': 'in',
  'el-GR': 'gr',
  'da-DK': 'dk',
  'nb-NO': 'no',
  'nl-BE': 'be',
  'pl-PL': 'pl',
  'ro-RO': 'ro',
  'sr-RS': 'rs',
};

// Reverse mapping: country codes to language codes
const COUNTRY_TO_LANGUAGE = {
  gb: 'en-GB',
  us: 'en-US',
  au: 'en-AU',
  ca: 'en-CA',
  fr: 'fr-FR',
  de: 'de-DE',
  es: 'es-ES',
  mx: 'es-MX',
  it: 'it-IT',
  br: 'pt-BR',
  in: 'hi-IN',
  gr: 'el-GR',
  dk: 'da-DK',
  no: 'nb-NO',
  be: 'nl-BE',
  pl: 'pl-PL',
  ro: 'ro-RO',
  rs: 'sr-RS',
};

/**
 * Converts language codes (e.g., "fr-FR", "en-GB") to country codes (e.g., "fr", "gb")
 * Falls back to input if already a country code or if no mapping exists
 * @param {string} languageCode - The language code to convert
 * @returns {string|null} The corresponding country code, or the input if no mapping exists
 */
export const languageToCountry = (languageCode) => {
  if (!languageCode) return null;
  return LANGUAGE_TO_COUNTRY[languageCode] || languageCode;
};

/**
 * Converts country codes (e.g., "fr", "gb") to language codes (e.g., "fr-FR", "en-GB")
 * Falls back to input if already a language code or if no mapping exists
 * @param {string} countryCode - The country code to convert
 * @returns {string|null} The corresponding language code, or the input if no mapping exists
 */
export const countryToLanguage = (countryCode) => {
  if (!countryCode) return null;
  return COUNTRY_TO_LANGUAGE[countryCode] || countryCode;
};

const SUPPORTED_LANGUAGE_CODES = Object.keys(LANGUAGE_TO_COUNTRY);

const CountryContext = React.createContext(undefined);

const isBrowser = () => typeof window !== 'undefined';

// Hook to safely track pathname changes in both SSR and browser
const useCurrentPathname = () => {
  const [pathname, setPathname] = React.useState(() => {
    return isBrowser() ? window.location.pathname : '/';
  });

  React.useEffect(() => {
    if (!isBrowser()) return;

    // Force immediate update to current pathname
    setPathname(window.location.pathname);

    // Update on various navigation events
    const updatePathname = () => {
      const currentPath = window.location.pathname;
      setPathname(currentPath);
    };

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', updatePathname);

    // Listen for Gatsby route changes
    window.addEventListener('gatsby-route-change', updatePathname);

    // Also check frequently for any missed changes
    const intervalId = setInterval(() => {
      const currentPath = window.location.pathname;
      if (currentPath !== pathname) {
        setPathname(currentPath);
      }
    }, 50); // Check every 50ms for faster updates

    return () => {
      window.removeEventListener('popstate', updatePathname);
      window.removeEventListener('gatsby-route-change', updatePathname);
      clearInterval(intervalId);
    };
  }, [pathname]);

  return pathname;
};

const trimSlashes = (value) =>
  typeof value === 'string' ? value.replace(/^\/+|\/+$/g, '') : '';

const ensureTrailingSlash = (path) => {
  if (!path || path === '/') {
    return '/';
  }
  return path.endsWith('/') ? path : `${path}/`;
};

export const getCountryFromPathname = (pathname) => {
  const trimmed = trimSlashes(pathname || '');
  if (!trimmed) {
    return null;
  }
  const [firstSegment] = trimmed.split('/');
  if (!firstSegment) {
    return null;
  }

  // NEW: Check if it's a language code (e.g., en-GB, fr-FR)
  if (LANGUAGE_TO_COUNTRY[firstSegment]) {
    return LANGUAGE_TO_COUNTRY[firstSegment];
  }

  // Legacy: Still support old country codes for compatibility
  return SUPPORTED_COUNTRIES.includes(firstSegment) ? firstSegment : null;
};

export const stripCountryFromPathname = (pathname) => {
  if (!pathname) {
    return '/';
  }
  const trimmed = trimSlashes(pathname || '');
  if (!trimmed) {
    return '/';
  }
  const [firstSegment, ...rest] = trimmed.split('/');

  // Check if first segment is a language code (e.g., en-GB)
  if (LANGUAGE_TO_COUNTRY[firstSegment]) {
    const remainder = rest.join('/');
    return remainder ? `/${remainder}` : '/';
  }

  // Check if first segment is a country code (legacy support)
  if (SUPPORTED_COUNTRIES.includes(firstSegment)) {
    const remainder = rest.join('/');
    return remainder ? `/${remainder}` : '/';
  }

  // No language or country code found
  return pathname || '/';
};

export const buildLocalizedPathname = (countryCode, pathname) => {
  const basePath = ensureTrailingSlash(
    stripCountryFromPathname(pathname || '/')
  );
  if (!countryCode || !SUPPORTED_COUNTRIES.includes(countryCode)) {
    return basePath || '/';
  }

  // Convert country code to language code for URL
  const languageCode = COUNTRY_TO_LANGUAGE[countryCode] || countryCode;

  if (basePath === '/' || basePath === '') {
    return `/${languageCode}/`;
  }
  const normalizedBase = basePath.startsWith('/') ? basePath : `/${basePath}`;
  return ensureTrailingSlash(`/${languageCode}${normalizedBase}`);
};

const getStoredCountry = () => {
  if (!isBrowser()) {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && COUNTRY_CONFIG[stored]) {
      return stored;
    }
  } catch (_) {
    /** ignore storage access errors */
  }
  return null;
};

const detectCountryFromNavigator = () => {
  if (!isBrowser()) {
    return null;
  }
  const navigatorLanguages = window.navigator?.languages || [];
  const candidates = [
    ...(navigatorLanguages || []),
    window.navigator?.language,
    window.navigator?.userLanguage,
  ]
    .filter(Boolean)
    .map((lang) => (lang ? String(lang).toLowerCase() : ''));

  for (const candidate of candidates) {
    const match = matchCountryByLanguage(candidate);
    if (match) {
      return match;
    }
  }
  return null;
};

const detectCountryFromPath = () => {
  if (!isBrowser()) {
    return null;
  }
  const pathname = window.location?.pathname;
  const country = getCountryFromPathname(pathname);
  if (country && COUNTRY_CONFIG[country]) {
    return country;
  }
  return null;
};

const detectCountryFromCookie = () => {
  if (!isBrowser()) {
    return null;
  }
  const cookieLanguage = readLanguageCookie();
  if (!cookieLanguage) {
    return null;
  }
  const matchedCountry = matchCountryByLanguage(cookieLanguage);
  if (matchedCountry && COUNTRY_CONFIG[matchedCountry]) {
    return matchedCountry;
  }
  clearLanguageCookie();
  return null;
};

const matchCountryByLanguage = (languageTag) => {
  if (!languageTag) {
    return null;
  }
  const normalized = languageTag.toLowerCase();

  const buildCandidates = (value) => {
    if (!value) {
      return [];
    }
    const normalizedValue = String(value).toLowerCase();
    const parts = normalizedValue.split('-');
    const candidates = [normalizedValue];
    if (parts.length > 1) {
      const base = parts[0];
      if (base) {
        candidates.push(base);
      }
    }
    return candidates;
  };

  for (const [countryCode, config] of Object.entries(COUNTRY_CONFIG)) {
    const { localeHints = [], language } = config;
    const candidates = new Set();

    for (const hint of localeHints) {
      for (const candidate of buildCandidates(hint)) {
        candidates.add(candidate);
      }
    }

    for (const candidate of buildCandidates(language)) {
      candidates.add(candidate);
    }

    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }
      if (normalized === candidate || normalized.startsWith(`${candidate}-`)) {
        return countryCode;
      }
    }
  }
  return null;
};

export const CountryProvider = ({ children }) => {
  // Track pathname changes safely in both SSR and browser
  const currentPathname = useCurrentPathname();

  const pathCountry = React.useMemo(
    () => getCountryFromPathname(currentPathname),
    [currentPathname]
  );
  const cookieCountry = React.useMemo(() => detectCountryFromCookie(), []);
  const storageCountry = React.useMemo(() => getStoredCountry(), []);
  const [country, setCountryState] = React.useState(() => {
    const initialPathCountry = detectCountryFromPath();
    if (initialPathCountry) {
      return initialPathCountry;
    }
    if (cookieCountry) {
      return cookieCountry;
    }
    if (storageCountry) {
      return storageCountry;
    }
    const detected = detectCountryFromNavigator();
    if (detected && COUNTRY_CONFIG[detected]) {
      return detected;
    }
    return DEFAULT_COUNTRY;
  });

  const hasStoredChoice = React.useRef(
    Boolean(pathCountry || cookieCountry || storageCountry)
  );

  // Update country state when pathname changes
  React.useEffect(() => {
    // Always update if there's a country in the path
    if (pathCountry) {
      setCountryState(pathCountry);
      hasStoredChoice.current = true;
    } else if (currentPathname === '/') {
      // If at root with no country, use the stored or default country
      const stored = getStoredCountry() || cookieCountry || DEFAULT_COUNTRY;
      if (stored !== country) {
        setCountryState(stored);
      }
    }
  }, [pathCountry, currentPathname, cookieCountry, country]); // Include all deps

  const setCountry = React.useCallback((nextCountry) => {
    if (!nextCountry || !COUNTRY_CONFIG[nextCountry]) {
      return;
    }
    setCountryState(nextCountry);
  }, []);

  React.useEffect(() => {
    if (!isBrowser()) {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, country);
      hasStoredChoice.current = true;
    } catch (_) {
      /** ignore storage write errors */
    }
  }, [country]);

  // gatsby-plugin-react-i18next handles language switching automatically
  // based on URL, so we don't need to manually change languages anymore

  const countryConfig =
    COUNTRY_CONFIG[country] || COUNTRY_CONFIG[DEFAULT_COUNTRY];

  const value = React.useMemo(
    () => ({
      country,
      setCountry,
      options: SUPPORTED_COUNTRIES,
      countryConfig,
    }),
    [country, countryConfig, setCountry]
  );

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
};

export const useCountry = () => {
  const context = React.useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};
